<script setup lang="ts">
/**
 * State management
 *
 * page = catalog
 * step 1 = active
 * step 1 = not completed
 * link of step 1 = catalog + diamond id if diamond is is set
 * image step 1 = setting icon (placeholder)
 *
 * step 2 = not active
 * step 2 = if diamond id is set, completed else not completed
 * link of step 2 = diamond search if no diamond id is set else diamond details page
 * image step 2 = if diamond id is set, diamond image else diamond placeholder
 *
 * step 3 = not active
 * step 3 = not completed
 * link of step 3 = no link
 * image step 3 = step 3 has no image
 *
 * page = diamond
 * step 1 = not active
 * step 1 = completed if conf session id is set else not completed
 * link of step 1 = catalog + diamond id if diamond id is set else only catalog
 * image step 1 = if conf session id is set, item image else setting icon placeholder
 *
 * step 2 = active
 * step 2 = completed if conf session id is set else not completed
 * link of step 2 = diamond search + conf session id if conf session id is set else only diamond search
 * image step 2 = diamond placeholder
 *
 * step 3 = not active
 * step 3 = not completed
 * link of step 3 = no link
 * image step 3 = step 3 has no image
 *
 * page = conf
 * step 1 = not active
 * step 1 = completed
 * link of step 1 = catalog + diamond id
 * image step 1 = conf session item image
 *
 * step 2 = not active
 * step 2 = completed
 * link of step 2 = diamond id in diamond details page + conf session id
 * image step 2 = diamond image
 *
 * step 3 = active
 * step 3 = completed
 * link of step 3 = conf + conf session id
 * image step 3 = step 3 has no image
 *
 */
import {computed, onMounted, ref, useSlots, watch} from "vue";
import {useStore} from "vuex";
import {Sessions} from "@/assets/ts/sessions";
import {SetWidget} from "@/assets/ts/stepWidget";
import DiamondImage from "@/components/diamonds/diamondImage.vue";
import {Diamond} from "@/store/modules/diamonds";
import DiamondRingIcon from "@/components/elements/diamondRingIcon.vue";
import DiamondIcon from "@/components/elements/diamondIcon.vue";
import NecklaceDiamondIcon from "@/components/elements/necklaceDiamondIcon.vue";

const store = useStore();
const slots = useSlots();

const sessionsApi = new Sessions();
const setWidgetApi = new SetWidget();

const emit = defineEmits(['certificatedDiamond']);

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  catalogId: {
    type: String,
    default: ""
  }
});

const session = computed(() => store.getters["session/get"]);
const sessionData = ref(session.value)

const confSession = computed(() => {
  return setWidgetApi.getConfSession();
});

const itemType = computed(() => {
  if (props.catalogId.includes('engagement_ring')) return 'ring';
  if (props.catalogId.includes('necklace')) return 'necklace';
  if (sessionData.value?.items?.[0]) {
    if (sessionData.value.items[0].facts.item_type === 'engagement_ring') return 'ring';
    if (sessionData.value.items[0].facts.item_type === 'necklace') return 'necklace';
  }
  return "default"
})

type stateStepType = {
  active: boolean,
  completed: boolean,
  view: string
  change: string
  image: boolean

}

type stateType = {
  step1: stateStepType,
  step2: stateStepType,
  step3: stateStepType
}

const state = ref({
  step1: {
    active: false,
    completed: false,
    view: '',
    change: '',
    image: false
  },
  step2: {
    active: false,
    completed: false,
    view: '',
    change: '',
    image: false
  },
  step3: {
    active: false,
    completed: false,
    view: '',
    change: '',
    image: false
  }
} as stateType)

const stateModeActive = ref(true)
watch(() => sessionData.value?.items?.[0]?.facts?.biggest_stone, (newVal) => {
  if (typeof newVal === 'undefined') {
    stateModeActive.value = true
  } else {
    stateModeActive.value = newVal >= 0.3
  }
}, {immediate: true})

onMounted(() => {
  if (confSession.value && session.value?.session_id !== confSession.value) {
    sessionsApi.getSimple(confSession.value).then((rawSessionData: any) => {
      sessionData.value = rawSessionData;
      if (sessionData.value?.items?.[0]?.facts?.certificated_diamond) {
        emit('certificatedDiamond', sessionData.value.items[0].facts.certificated_diamond);
      }
    });
  } else {
    if (session.value?.items?.[0]?.facts?.certificated_diamond) {
      emit('certificatedDiamond', session.value.items[0].facts.certificated_diamond);
    }
  }

  setState()
  store.dispatch('diamonds/fetchDiamond', setWidgetApi.getDiamondId())
});

watch(session, (newVal) => {
  sessionData.value = newVal;
  setState();
}, {deep: true})

/**
 * item and diamond data
 */

const itemData = computed(() => {
  return sessionData.value?.items?.[0] || null;
})

const diamondData = computed<Diamond | null>(() => store.getters['diamonds/getDiamondById'](setWidgetApi.getDiamondId()))


/**
 * state management
 */

const isDiamondIdSet = computed(() => {
  return diamondItem.value !== "" && diamondItem.value !== null;
});

const getPage = (): string => {
  const path = document.location.href;
  const hash = document.location.hash;

  if (path.includes('catalog') || (hash.includes('conf/') && !isDiamondIdSet.value)) {
    return "catalog";
  } else if (path.includes('diamonds')) {
    return "diamond";
  } else if (hash.includes('conf/') && isDiamondIdSet.value) {
    return "conf";
  }

  return "";
}

const setState = () => {
  setActiveStep()
  setCompletedStep()
}

const setActiveStep = () => {
  const page = getPage();

  if (page === 'catalog') {
    state.value.step1.active = true
    state.value.step2.active = false
    state.value.step3.active = false
  } else if (page === 'diamond') {
    state.value.step1.active = false
    state.value.step2.active = true
    state.value.step3.active = false
  } else if (page === 'conf') {
    state.value.step1.active = false
    state.value.step2.active = false
    state.value.step3.active = true
  }
}

const setCompletedStep = () => {
  const confSession = setWidgetApi.getConfSession();
  const diamondItem = itemData.value?.facts.certificated_diamond.check ? itemData.value?.facts.certificated_diamond.certificate : setWidgetApi.getDiamondId();

  state.value.step1.completed = confSession !== null;
  if (state.value.step1.completed) {
    state.value.step1.change = setWidgetApi.createPath('catalog', diamondItem);
    state.value.step1.view = setWidgetApi.createPath('conf');
    state.value.step1.image = true
  } else {
    state.value.step1.view = ""
    state.value.step1.change = ""
  }

  state.value.step2.completed = (confSession === null && diamondItem !== null) || (confSession !== null && diamondItem !== null);
  if (state.value.step2.completed) {
    state.value.step2.view = setWidgetApi.createPath('diamond', diamondItem);
    state.value.step2.change = setWidgetApi.createPath('diamondSearch');
    state.value.step2.image = true
  } else {
    state.value.step2.view = ""
    state.value.step2.change = ""
    state.value.step2.image = false
  }

  state.value.step3.completed = confSession !== null && itemData.value?.facts.certificated_diamond.check && isDiamondAvailable.value;
}

const isDiamondAvailable = computed(() => {
  return typeof itemData.value?.facts.certificated_diamond == "undefined" || itemData.value?.facts.certificated_diamond.available;
});

const diamondItem = computed(() => {
  const idx = 0
  if (session.value?.items && session.value?.items[idx]?.facts.certificated_diamond.check) {
    return session.value?.items[idx].facts.certificated_diamond.certificate
  } else if (setWidgetApi.getDiamondId() !== "") {
    return setWidgetApi.getDiamondId()
  }
  return ""
});

const formMap: Record<string, string> = {
  'ROUND': 'BR',
  'OVAL': 'OV',
  'EMERALD': 'EM',
  'PEAR': 'PE',
  'PRINCESS': 'PR',
  'RADIANT': 'RA',
  'HEART': 'HE',
  'MARQUISE': 'MA',
  'CUSHION': 'CU'
};

</script>

<template>
  <div class="w-full bg-white border-b border-gray-100 shadow-sm sticky top-0 z-50">
    <div class="max-w-screen-4xl mx-auto flex items-stretch overflow-hidden"
         :class="{
      'h-16 lg:h-20': !slots.default,
      'h-16 lg:h-24': slots.default
    }"
    >

      <!-- catalog image Section -->
      <div v-if="slots.default" class="flex flex-col justify-center border-r border-gray-50 min-w-max lg:min-w-[1/4]">
        <slot/>
      </div>

      <!-- Steps Section -->
      <div v-if="show" class="flex-1 grid grid-cols-3 items-stretch bg-gray-100 gap-px overflow-x-auto">

        <div
            class="flex items-center justify-center px-2 lg:px-6 relative group transition-all duration-300 step-container"
            :class="{'active': state.step1.active, 'completed': state.step1.completed}"
        >
          <div class="flex w-full items-center space-x-2 lg:space-x-4">
            <div
                class="w-7 h-7 lg:w-9 lg:h-9 rounded-full flex items-center justify-center border-2 transition-all duration-300"
                :class="[state.step1.active ? 'border-white bg-white text-black' : (state.step1.completed ? 'border-brand-main text-brand-main' : 'border-gray-100 text-gray-300 group-hover:border-gray-200')]"
            >
              <span v-if="state.step1.completed" class="text-xs lg:text-sm font-bold">✓</span>
              <span v-else class="text-xs lg:text-sm font-bold">1</span>
            </div>
            <div class="flex flex-col min-w-0 grow">
              <span
                  class="text-[9px] lg:text-[11px] font-bold uppercase tracking-wider truncate transition-colors"
                  :class="state.step1.active ? 'text-black font-bold' : 'text-gray-400 group-hover:text-gray-600'"
              >
                <template v-if="state.step1.completed">
                  {{ $t('components.stepWidget.setting.' + itemType) }} <span class="hidden sm:inline-block"
                                                                              v-if="sessionData?.items?.[0]?.price?.parts?.item">
                  <template v-if="sessionData.items[0].price.parts.stones == 0">
                    {{ $t('components.stepWidget.including_diamond') }}
                  </template>
                  <template
                      v-else-if="typeof sessionData.items[0].price.parts?.shoulder_stones !== 'undefined' && sessionData.items[0].price.parts?.shoulder_stones != 0">
{{ $t('components.stepWidget.including_shoulder_stones') }}
                  </template>

                  <template
                      v-if="typeof sessionData.items[0].price.parts?.shoulder_stones !== 'undefined' && sessionData.items[0].price.parts?.shoulder_stones >= 0">
                    - {{
                      $n(sessionData.items[0].price.parts.item + sessionData.items[0].price.parts?.shoulder_stones, 'currency')

                    }}
                  </template>
                  <template v-else>
                  - {{
                      $n(sessionData.items[0].price.parts.item, 'currency')

                    }}
                </template></span>

                </template>
                <template v-else>
                  {{ $t('components.stepWidget.setting.' + itemType) }}
                </template>
              </span>
              <span v-if="state.step1.view || state.step1.change"
                    class="text-[8px] lg:text-[10px] font-medium truncate flex flex-col sm:flex-row sm:items-center space-x-2"
                    :class="state.step1.active ? 'text-black' : 'text-brand-main'">
                <a v-if="state.step1.change" :href="state.step1.change"
                   class="hover:underline uppercase">{{ $t('components.stepWidget.change') }}</a>
                <a v-if="state.step1.view" :href="state.step1.view"
                   class="hover:underline uppercase">{{ $t('components.stepWidget.view') }}</a>
              </span>
              <a v-else :href="setWidgetApi.createPath('catalog', setWidgetApi.getDiamondId())"
                 class="text-[8px] lg:text-[10px] font-medium truncate hidden sm:block"
                 :class="state.step1.active ? 'text-black' : 'text-gray-400'">
                {{ $t('components.stepWidget.browse_settings') }}
              </a>
            </div>

            <!-- Setting Example Image -->
            <div v-if="state.step1.image" class="hidden lg:block ml-4 my-auto">
              <div v-if="itemData?.image"
                   class="relative w-16 h-16 rounded-full overflow-hidden flex items-center justify-center border border-gray-200">
                <img v-if="itemData.image.filename_layer" :src="itemData.image.filename_layer"
                     class="absolute insert-0 w-auto h-full aspect-square object-cover"
                     alt="Setting image"/>
                <img :src="itemData.image.filename"
                     class="w-auto h-full aspect-square object-cover"
                     alt="Setting image"/>
              </div>
            </div>
            <div v-else class="hidden lg:block ml-4 my-auto items-center justify-center">
              <div class="relative w-16 h-16 rounded-full overflow-hidden flex items-center justify-center p-1">

                <necklace-diamond-icon v-if="itemType == 'necklace'"/>
                <diamond-ring-icon v-else/>
              </div>
            </div>

          </div>
        </div>

        <!-- Step 2: Choose Diamond -->
        <div
            class="flex items-center justify-center px-2 lg:px-6 relative group transition-all duration-300 step-container"
            :class="{'active': state.step2.active, 'completed': state.step2.completed}"
        >
          <div class="flex w-full items-center space-x-2 lg:space-x-4 pl-4">
            <div
                class="w-7 h-7 lg:w-9 lg:h-9 rounded-full flex items-center justify-center border-2 transition-all duration-300 shrink-0"
                :class="[state.step2.active ? 'border-white bg-white text-black' : (state.step2.completed ? 'border-brand-main text-brand-main' : 'border-gray-100 text-gray-300 group-hover:border-gray-200')]"
            >
              <span v-if="state.step2.completed " class="text-xs lg:text-sm font-bold">✓</span>
              <span v-else class="text-xs lg:text-sm font-bold">2</span>
            </div>
            <div class="flex flex-col min-w-0 grow">
              <span
                  class="text-[9px] lg:text-[11px] font-bold uppercase tracking-wider truncate transition-colors"
                  :class="state.step2.active ? 'text-black font-bold' : 'text-gray-400 group-hover:text-gray-600'"
              >
                <template v-if="state.step2.completed">
                  {{ $t('components.stepWidget.diamond') }}
                  <template v-if="diamondData?.price">
                    <span class="hidden sm:inline-block"> - {{
                        $n(diamondData?.price, 'currency')
                      }}</span>
                  </template>
                  <template v-else>
                    <span class="hidden sm:inline-block"
                          v-if="sessionData?.items?.[0]?.price?.parts?.stones"> - {{
                        $n(sessionData.items[0].price.parts.stones, 'currency')
                      }}</span>
                  </template>
                </template>
                <template v-else>
                  {{ $t('components.stepWidget.choose_diamond') }}
                </template>
              </span>
              <span v-if="state.step2.completed"
                    class="text-[8px] lg:text-[10px] font-medium flex flex-col sm:flex-row sm:items-center space-x-2"
                    :class="state.step2.active ? 'text-black' : 'text-brand-main'">
                <a @click.stop :href="state.step2.change"
                   class="hover:underline uppercase">{{ $t('components.stepWidget.change') }}</a>

                <template v-if="!isDiamondAvailable">
                  <span class="text-red-500 font-bold">{{
                      $t('components.stepWidget.diamondNotAvailable')
                    }}</span>
                </template>
                <template v-else>
                  <a @click.stop :href="state.step2.view" class="hover:underline uppercase">{{
                      $t('components.stepWidget.view')
                    }}</a>
                </template>
              </span>
              <span v-else-if="!stateModeActive"
                    class="text-[8px] lg:text-[10px] font-medium flex items-center space-x-2">
                {{ $t('components.stepWidget.min_diamond_size') }}
              </span>
              <a v-else :href="setWidgetApi.createPath('diamondSearch')"
                 class="text-[8px] lg:text-[10px] font-medium truncate hidden sm:block"
                 :class="state.step2.active ? 'text-black' : 'text-gray-400'">
                {{ $t('components.stepWidget.browse_diamonds') }}
              </a>
            </div>

            <!-- Diamond Example Image -->
            <div v-if="state.step2.image" class="hidden lg:block ml-4 my-auto">
              <div
                  class="relative w-16 h-16  rounded-full overflow-hidden flex items-center justify-center border border-gray-200">
                <diamond-image
                    :diamond="{form: formMap[sessionData.items[0]?.facts.certificated_diamond.current.form] || diamondData?.form || 'BR'}"
                    :alert="false"/>
              </div>
            </div>
            <div v-else class="hidden lg:block ml-4 my-auto items-center justify-center">
              <div class="relative w-16 h-16 rounded-full overflow-hidden flex items-center justify-center p-4">
                <diamond-icon/>
              </div>
            </div>

          </div>
        </div>

        <!-- Step 3: Complete Ring -->
        <div
            class="flex items-center justify-center px-2 lg:px-6 relative cursor-pointer group transition-all duration-300 step-container"
            :class="{'active': state.step3.active, 'completed': state.step3.completed}"
        >
          <div class="flex w-full justify-between items-center space-x-2 lg:space-x-4 pl-4">
            <div
                class="w-7 h-7 lg:w-9 lg:h-9 rounded-full flex items-center justify-center border-2 transition-all duration-300 shrink-0"
                :class="[state.step3.active ? 'border-white bg-white text-black' : (state.step3.completed ? 'border-brand-main text-brand-main' : 'border-gray-100 text-gray-300 group-hover:border-gray-200')]"
            >
              <span v-if="state.step3.completed" class="text-xs lg:text-sm font-bold">✓</span>
              <span v-else class="text-xs lg:text-sm font-bold">3</span>
            </div>
            <div class="flex flex-col min-w-0 grow">
              <span
                  class="text-[9px] lg:text-[11px] font-bold uppercase tracking-wider transition-colors"
                  :class="state.step3.active ? 'text-black font-bold' : 'text-gray-400 group-hover:text-gray-600'"
              >
                {{ $t('components.stepWidget.complete_ring.' + itemType) }}
              </span>
              <span class="text-[8px] lg:text-[10px] font-medium truncate hidden sm:block"
                    :class="state.step3.active ? 'text-black' : 'text-gray-400'">
                {{ $t('components.stepWidget.select_ring_size.' + itemType) }}
              </span>
            </div>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<style scoped>
.font-ivymode {
  font-family: "Ivy Mode", serif;
}

.step-container {
  background-color: white;
  transition: background-color 0.3s;
  clip-path: polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%, 15px 50%);
  margin-left: -15px;
}

.step-container.active {
  background-color: #eaded5;
  z-index: 10;
}

.step-container:first-child {
  margin-left: 0;
  clip-path: polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%);
}

.step-container:last-child {
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 15px 50%);
}

.step-container:hover {
  background-color: #f9f9f9;
}
</style>